/**
 * Root route — owns the page shell *and* the session guard.
 *
 * `beforeLoad` runs on every navigation before the matched leaf renders. It
 * resolves the current session via `queryClient.ensureQueryData(meQueryOptions)`
 * (one wire call shared with React-side `useQuery` consumers) and routes by
 * pathname into one of three regions:
 *
 *   - `/sign-in`, `/sign-in/otp`           — pre-auth funnel; no session required
 *   - `/onboarding/...`                     — post-OTP, pre-workspace
 *   - everything else                       — workspace; requires session + companyId
 *
 * Anonymous visitors hitting workspace pages are bounced to `/sign-in`.
 * Half-onboarded users (session, no company) are bounced to
 * `/onboarding/workspace`. Fully signed-in users hitting the auth funnel are
 * bounced back to `/`. The check is the single source of truth for "is this
 * navigation allowed?" — leaves can rely on the principal being correct by
 * the time they render.
 */
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { meQueryOptions } from "../lib/session.ts";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient
      .ensureQueryData(meQueryOptions)
      // Treat any `identity.me` failure as "no session" — the API is up
      // (the request reached it) but the response wasn't usable. Falling
      // through to the unauthenticated branch sends the user to /sign-in,
      // which is the right recovery for stale or missing cookies.
      .catch(() => null);

    const path = location.pathname;
    const inAuthFunnel = path === "/sign-in" || path.startsWith("/sign-in/");
    const inOnboarding = path === "/onboarding" || path.startsWith("/onboarding/");
    const inOnboardingAccount = path === "/onboarding/account";
    const inOnboardingHandoff = path === "/onboarding/tally" || path === "/onboarding/done";
    // `/join/<token>` is the team-invite landing page — public for the
    // anonymous case AND reachable while signed in (the page itself
    // shows a "sign out to continue" prompt when the visitor is in the
    // wrong session). It's the one screen that should never bounce.
    const inJoin = path === "/join" || path.startsWith("/join/");

    if (!me) {
      // Anonymous visitor. The auth funnel + the join landing are the
      // only allowed surfaces; onboarding routes need a session, and
      // workspace routes need both a session and a company.
      if (inAuthFunnel || inJoin) return;
      throw redirect({ to: "/sign-in" });
    }

    // Profile-complete gate (ADR-0011 §3.5). Any user past OTP must
    // have set a real name + email before the workspace surface — and
    // before accepting an invite — becomes reachable. The join page
    // catches the same case inline (so invitees never leave the join
    // URL); this guard is the safety net for everything else.
    //
    // Allowed when `!profileComplete`: the auth funnel (so a stuck
    // user can re-OTP), the join landing (the page itself prompts for
    // name+email before calling acceptInvite), and the
    // `/onboarding/account` page itself.
    if (!me.user.profileComplete) {
      if (inAuthFunnel || inJoin || inOnboardingAccount) return;
      throw redirect({ to: "/onboarding/account" });
    }

    if (!me.company) {
      // OTP-verified, profile complete, but workspace not yet created.
      // Account/workspace onboarding pages, the sign-in funnel, and the join
      // landing stay reachable. Import/done need a real company.
      if (inOnboardingHandoff) throw redirect({ to: "/onboarding/workspace" });
      if (inOnboarding || inAuthFunnel || inJoin) return;
      throw redirect({ to: "/onboarding/workspace" });
    }

    // Subscription lockout (ADR-0047). Proactive counterpart to the reactive
    // `subscription-ended` redirect in `query-client.ts` — a page that
    // renders off cached data or only calls allowlisted procedures (which
    // `identity.me` is) would otherwise leave the user inside the shell of
    // a locked-out workspace. Only the paywall + billing settings stay
    // reachable so the tenant can re-subscribe; sign-in and join are the
    // usual escape hatches.
    const subStatus = me.company.subscription?.status;
    const isLockedOut = subStatus === "hard_suspended" || subStatus === "canceled";
    const inPaywall = path === "/subscription-ended";
    const inBillingSettings =
      path === "/settings/billing" ||
      path.startsWith("/settings/billing/") ||
      path === "/settings/subscription" ||
      path.startsWith("/settings/subscription/");
    if (isLockedOut && !inPaywall && !inBillingSettings && !inAuthFunnel && !inJoin) {
      throw redirect({ to: "/subscription-ended" });
    }

    // Full session: workspace user. Auth funnel and pre-company onboarding
    // pages are off-limits, but the post-create import/done handoff remains
    // reachable so S03 can navigate to S04/S05 after membership creation.
    if (inJoin) return;
    if (inAuthFunnel || (inOnboarding && !inOnboardingHandoff)) {
      // Signed-in users re-entering the funnel land on the app's front
      // door — the module launcher (ADR-0049).
      throw redirect({ to: "/launch" });
    }
  },
  component: RootShell,
});

function RootShell() {
  return (
    <div className="min-h-screen bg-bg text-ink">
      <Outlet />
    </div>
  );
}
